import { Action, Reducer } from 'redux';
import {
  ACTION, AddInstance, ClearInstance, ReplaceRiskReportResults, RequestOptionsDrillDownLayoutTypesTo, RequestOptionsDrillDownTo, RequestRiskReportResults,
  ResponseOptionsDrillDownLayoutTypesTo,
  ResponseOptionsDrillDownTo
} from './../risk-listing/action-types';
import { RiskReportInstanceState, RiskReportStoreState } from './types-store';
import { RenderMode } from 'app/types-shared';
import { mapRiskMatrixDefinitionToStoreFormat } from 'app/area/case/features/risk-matrix/risk-matrix-definition';
import { convertRiskMatrixTypeResult } from 'app/area/case/features/risk-matrix';

export const INITIAL_RISK_REPORT_INSTANCE_STATE: RiskReportInstanceState = {
  renderMode: RenderMode.report,
  fetching: false,
  requestId: 0,
  riskReportResults: undefined,
  errorsExecuteFailed: undefined
};

export const INITIAL_RISK_REPORT_STATE: RiskReportStoreState = {
  instances: {},
  texts: {} as any
};

function transform(
  state: RiskReportStoreState,
  instance: string,
  requestId: number | undefined,
  transformation: (riskReport: RiskReportInstanceState) => RiskReportInstanceState): RiskReportStoreState {
  return {
    ...state,
    instances: Object.keys(state.instances).reduce((aggr, key) => {
      if (key === instance && (!requestId || state.instances[key].requestId === requestId)) {
        aggr[key] = transformation(state.instances[key]);
      }
      else {
        aggr[key] = state.instances[key];
      }
      return aggr;
    }, {})
  };
}

const reducers: { [indexer: string]: Reducer<RiskReportStoreState, Action> } = {
  /**
   * Run when finished fetching data for risk report favourite.
   */
  [ACTION.REPLACE_RISK_REPORT_RESULTS]: (state: any, action: ReplaceRiskReportResults): RiskReportStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => ({
        ...i,
        fetching: false,
        errorsExecuteFailed: undefined,
        riskReportResults: {
          ...action.riskReportResults,
          layoutModel: action.layoutModel,
          matrixDefs: action.riskReportResults.matrixDefs.map(m => mapRiskMatrixDefinitionToStoreFormat(m)),
          results: action.riskReportResults.results.map(result => ({
            ...result,
            riskMatrices: result.riskMatrices.map(riskMatrix => convertRiskMatrixTypeResult(
              riskMatrix,
              mapRiskMatrixDefinitionToStoreFormat(
                action.riskReportResults.matrixDefs.find(matrixDef => matrixDef.type === riskMatrix.riskMatrixType)
              )
            )
            )
          })
          )
        }
      })
    );
  },

  /**
   * Run when starting fetching data for a case report favourite.
   */
  [ACTION.REQUEST_RISK_REPORT_RESULTS]: (state: RiskReportStoreState, action: RequestRiskReportResults): RiskReportStoreState => {

    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        fetching: true,
        requestId: i.requestId + 1
      })
    );
  },

  [ACTION.ADD_INSTANCE]: (state: RiskReportStoreState, action: AddInstance): RiskReportStoreState => {
    return {
      ...state,
      instances: {
        ...state.instances,
        [action.instanceKey]: {
          ...INITIAL_RISK_REPORT_INSTANCE_STATE
        }
      }
    };
  },

  [ACTION.CLEAR_INSTANCE]: (state: RiskReportStoreState, action: ClearInstance): RiskReportStoreState => {

    return {
      ...state,
      instances: Object.keys(state?.instances).reduce(
        (aggr, key) => {
          if (key !== action.instanceKey) {
            aggr[key] = state.instances[key];
          }
          return aggr;
        }, {}
      )
    };
  },

  [ACTION.REQUEST_OPTIONS_DRILL_DOWN_TO]: (state: RiskReportStoreState,
    action: RequestOptionsDrillDownTo
  ): RiskReportStoreState => {
    return transform(state, action.instanceKey, undefined,
      i => ({
        ...i,
        fetchingOptionsDrillDownTo: true
      })
    );
  },

  [ACTION.RESPONSE_OPTIONS_DRILL_DOWN_TO]: (state: RiskReportStoreState,
    action: ResponseOptionsDrillDownTo
  ): RiskReportStoreState => {
    return transform(state, action.instanceKey, undefined,
      i => ({
        ...i,
        fetchingOptionsDrillDownTo: false,
        optionsDrillDownTo: action.data
      })
    );
  },

  [ACTION.REQUEST_OPTIONS_DRILL_DOWN_LAYOUT_TYPES_TO]: (state: RiskReportStoreState,
    action: RequestOptionsDrillDownLayoutTypesTo
  ): RiskReportStoreState => {
    return transform(state, action.instanceKey, undefined,
      i => ({
        ...i,
        fetchingLayoutTypesDrillDownTo: true
      })
    );
  },

  [ACTION.RESPONSE_OPTIONS_DRILL_DOWN_LAYOUT_TYPES_TO]: (state: RiskReportStoreState,
    action: ResponseOptionsDrillDownLayoutTypesTo
  ): RiskReportStoreState => {
    return transform(state, action.instanceKey, undefined,
      i => ({
        ...i,
        fetchingLayoutTypesDrillDownTo: false,
        optionsLayoutTypesDrillDownTo: action.data
      })
    );
  }
}; 

export const reducer: Reducer<RiskReportStoreState> = (state: RiskReportStoreState = INITIAL_RISK_REPORT_STATE, action: any) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};
